import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HotelSearchQuery } from 'generated/graphql';

// Error types for better error handling
export class RoomConfigurationValidationError extends Error {
  constructor(
    message: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'RoomConfigurationValidationError';
  }
}

export type ValidationResult = {
  isValid: boolean;
  errors: string[];
};

type AmadeusHotel = HotelSearchQuery['hotelSearch']['results'][number]['hotel'];

export type Hotel = {
  amadeusHotelId: string;
  id: string | null | undefined;
  formattedAddress: string | null | undefined;
  imageUrl: string | null | undefined;
  name: string;
  starRating: number;
};

export type RoomTypeQuantities = {
  single: number;
  double: number;
  suite: number;
};

export type RoomConfiguration = {
  rooms: number;
  guests: number;
};

export type RoomConfigurations = {
  single: RoomConfiguration[];
  double: RoomConfiguration[];
  suite: RoomConfiguration[];
};

export const roomTypes: RoomType[] = ['single', 'double', 'suite'];
export type RoomType = keyof RoomTypeQuantities;

export interface GroupDetailsState {
  searchParams: string;
  shortList: Hotel[];
  quantities: RoomTypeQuantities;
  roomConfigurations?: RoomConfigurations;
}

export const initialState: GroupDetailsState = {
  searchParams: '',
  shortList: [],
  quantities: {
    single: 0,
    double: 0,
    suite: 0,
  },
};

// Validation helper functions
const validateSingleRoomConfiguration = (
  config: RoomConfiguration,
): string[] => {
  const errors: string[] = [];

  if (!Number.isInteger(config.rooms) || config.rooms < 0) {
    errors.push(
      `Invalid room count: ${config.rooms}. Must be a non-negative integer.`,
    );
  }

  if (
    !Number.isInteger(config.guests) ||
    config.guests < 1 ||
    config.guests > 2
  ) {
    errors.push(`Invalid guest count: ${config.guests}. Must be 1 or 2.`);
  }

  return errors;
};

const validateRoomConfigurationsPayload = (
  payload: RoomConfigurations,
): ValidationResult => {
  const errors: string[] = [];

  // Validate single room configurations
  payload.single.forEach((config, index) => {
    const configErrors = validateSingleRoomConfiguration(config);
    configErrors.forEach((error) => {
      errors.push(`Single room config ${index}: ${error}`);
    });
  });

  // Validate double room configurations
  payload.double.forEach((config, index) => {
    const configErrors = validateSingleRoomConfiguration(config);
    configErrors.forEach((error) => {
      errors.push(`Double room config ${index}: ${error}`);
    });
  });

  // Validate suite room configurations
  payload.suite.forEach((config, index) => {
    const configErrors = validateSingleRoomConfiguration(config);
    configErrors.forEach((error) => {
      errors.push(`Suite room config ${index}: ${error}`);
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const groupDetails = createSlice({
  name: 'groupDetails',
  initialState,
  reducers: {
    setSearchParams: (state, { payload }: PayloadAction<string>) => {
      state.searchParams = payload;
    },
    setQuantities: (
      state,
      { payload }: PayloadAction<GroupDetailsState['quantities']>,
    ) => {
      state.quantities = payload;
    },
    setRoomConfigurations: (
      state,
      { payload }: PayloadAction<RoomConfigurations>,
    ) => {
      const validationResult = validateRoomConfigurationsPayload(payload);

      if (validationResult.isValid) {
        state.roomConfigurations = payload;
      } else {
        // Throw a proper error instead of silent failure
        throw new RoomConfigurationValidationError(
          'Invalid room configurations provided',
          {
            errors: validationResult.errors,
            payload: payload,
          },
        );
      }
    },
    addToShortList: (state, { payload }: PayloadAction<AmadeusHotel>) => {
      const selected = {
        amadeusHotelId: payload.amadeusHotelId,
        id: payload.id,
        formattedAddress: payload.formattedAddress,
        imageUrl: payload.imageUrl,
        name: payload.name,
        starRating: payload.starRating,
      };

      state.shortList.push(selected);
    },
    removeFromShortList: (state, { payload }: PayloadAction<string>) => {
      const amadeusHotelId = payload;
      const nextShortList = state.shortList.filter(
        (hotel) => hotel.amadeusHotelId !== amadeusHotelId,
      );
      state.shortList = nextShortList;
    },
    reset: () => {
      return initialState;
    },
  },
});

export const {
  addToShortList,
  removeFromShortList,
  setQuantities,
  setRoomConfigurations,
  setSearchParams,
  reset,
} = groupDetails.actions;

export const groupDetailsReducer = groupDetails.reducer;
